import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useMutation } from "@tanstack/react-query";
import { AlertTriangle, ArrowLeft, ArrowRight, Download, Loader2, Shuffle } from "lucide-react";
import {
  translateReport,
  type CognosReportIR,
  type TargetPlatform,
  type TranslationResult,
} from "@/lib/api";
import { downloadTranslationManifest } from "@/lib/exports";
import { getLastIr, setLastTranslation } from "@/lib/workflow";

const TARGETS: { value: TargetPlatform; label: string }[] = [
  { value: "POWERBI_PREMIUM", label: "Power BI Premium" },
  { value: "TABLEAU_CLOUD", label: "Tableau Cloud" },
  { value: "QLIK_SENSE", label: "Qlik Sense" },
];

export function TranslatePage() {
  const navigate = useNavigate();
  const [target, setTarget] = useState<TargetPlatform>("POWERBI_PREMIUM");
  const [ir] = useState<CognosReportIR | null>(getLastIr());

  const mutation = useMutation<TranslationResult, Error>({
    mutationFn: () => translateReport(ir!, target),
    onSuccess: (t) => setLastTranslation(t),
  });

  if (!ir) {
    return <NoIrEmptyState />;
  }

  return (
    <div className="mx-auto max-w-5xl px-6 py-8">
      <header className="mb-6">
        <h1 className="font-display text-2xl font-semibold text-bridge-ink">
          Translate — {ir.displayName}
        </h1>
        <p className="mt-0.5 text-sm text-bridge-slate">
          Pick a target platform; Bridge emits a structured manifest plus an
          explicit list of unmapped Cognos concepts.
        </p>
      </header>

      <section data-tour-id="translate-target" className="grid gap-3 rounded-xl2 border border-bridge-slate/20 bg-white p-4 shadow-card">
        <div className="flex flex-wrap gap-2">
          {TARGETS.map((t) => (
            <button
              key={t.value}
              onClick={() => setTarget(t.value)}
              className={`rounded-md border px-3 py-1.5 text-xs font-medium ${
                target === t.value
                  ? "border-bridge-primary bg-bridge-primary text-white"
                  : "border-bridge-slate/30 bg-white text-bridge-ink hover:bg-bridge-mist"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => navigate("/parse")}
            className="inline-flex items-center gap-1 rounded-md border border-bridge-slate/30 bg-white px-3 py-1.5 text-xs font-medium text-bridge-ink hover:bg-bridge-mist"
          >
            <ArrowLeft size={12} /> Back to parse
          </button>
          <button
            onClick={() => mutation.mutate()}
            disabled={mutation.isPending}
            className="inline-flex items-center gap-1 rounded-md bg-bridge-primary px-3 py-1.5 text-xs font-medium text-white shadow-sm hover:bg-bridge-primaryDark disabled:opacity-40"
          >
            {mutation.isPending
              ? <Loader2 size={12} className="animate-spin" />
              : <Shuffle size={12} />}
            Translate
          </button>
          {mutation.isSuccess && mutation.data && (
            <>
              <button
                type="button"
                onClick={() => downloadTranslationManifest(mutation.data!)}
                title="Download the translation manifest as JSON for review or hand-off."
                className="inline-flex items-center gap-1 rounded-md border border-bridge-slate/30 bg-white px-3 py-1.5 text-xs font-medium text-bridge-ink hover:bg-bridge-mist"
              >
                <Download size={12} /> Download manifest
              </button>
              <button
                onClick={() => navigate("/validate")}
                className="inline-flex items-center gap-1 rounded-md border border-bridge-slate/30 bg-white px-3 py-1.5 text-xs font-medium text-bridge-ink hover:bg-bridge-mist"
              >
                Continue to validate <ArrowRight size={12} />
              </button>
            </>
          )}
        </div>
      </section>

      {mutation.error && (
        <p className="mt-4 rounded-md border border-bridge-danger/30 bg-bridge-danger/5 p-3 text-sm text-bridge-danger">
          Translate failed — {mutation.error.message}
        </p>
      )}
      {mutation.data && <TranslationView t={mutation.data} />}
    </div>
  );
}

function TranslationView({ t }: { t: TranslationResult }) {
  return (
    <section data-tour-id="translate-output" className="mt-4 rounded-xl2 border border-bridge-slate/20 bg-white p-4 shadow-card">
      <div className="flex items-end justify-between">
        <div>
          <h2 className="font-display text-base font-semibold text-bridge-ink">
            {t.target}
          </h2>
          <p className="mt-0.5 text-2xs text-bridge-slate">
            artifact: <span className="font-mono">{t.artifactName}</span>
          </p>
        </div>
        <ConfidenceBadge value={t.confidence} />
      </div>

      {t.notes.length > 0 && (
        <ul className="mt-3 space-y-1 text-xs text-bridge-ink/80">
          {t.notes.map((n, i) => <li key={i}>— {n}</li>)}
        </ul>
      )}

      {t.unmappedConcepts.length > 0 && (
        <div className="mt-3">
          <h3 className="text-2xs font-medium uppercase tracking-wide text-bridge-slate">
            Unmapped concepts ({t.unmappedConcepts.length})
          </h3>
          <ul className="mt-1 space-y-2 text-xs">
            {t.unmappedConcepts.map((u, i) => (
              <li key={i} className="rounded-md border border-bridge-warn/30 bg-bridge-warn/5 p-2">
                <div className="flex items-center gap-1.5">
                  <AlertTriangle size={12} className="text-bridge-warn" aria-hidden />
                  <span className="font-medium text-bridge-ink">{u.category}</span>
                  <span className="font-mono text-2xs text-bridge-slate">{u.sourceRef}</span>
                </div>
                <p className="mt-1 text-bridge-ink/80">{u.reason}</p>
                {u.fixHint && (
                  <p className="mt-0.5 text-2xs text-bridge-slate">→ {u.fixHint}</p>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}
    </section>
  );
}

function ConfidenceBadge({ value }: { value: number }) {
  const pct = Math.round(value * 100);
  const cls = value >= 0.7
    ? "bg-bridge-success/10 text-bridge-success border-bridge-success/30"
    : value >= 0.4
      ? "bg-bridge-warn/10 text-bridge-warn border-bridge-warn/30"
      : "bg-bridge-danger/10 text-bridge-danger border-bridge-danger/30";
  return (
    <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-2xs font-medium ${cls}`}>
      {pct}% confidence
    </span>
  );
}

function NoIrEmptyState() {
  return (
    <div className="mx-auto max-w-3xl px-6 py-12">
      <div className="rounded-xl2 border border-dashed border-bridge-slate/30 bg-white p-8 text-center">
        <p className="text-sm font-medium text-bridge-ink">No parsed IR in this session.</p>
        <p className="mt-1 text-2xs text-bridge-slate">
          Visit the Parse page first to ingest a Cognos report.
        </p>
      </div>
    </div>
  );
}
